/**
 * Защита от повторных нажатий на покупку (30.10.2026).
 *
 * Было: пока мобильный браузер грузит страницу кассы (а это секунды), сайт
 * остаётся живым, кнопка или текст замка снова нажимается — и каждый тап
 * заводит в ЮKassa ещё один платёж (три платежа «Твой месяц» за 2 секунды).
 *
 * Стало: одна покупка за раз на всю страницу.
 *   - Повторный вызов, пока платёж создаётся, получает ТОТ ЖЕ результат
 *     (тот же промис) — второго платежа нет, оба уходят по одной ссылке.
 *   - С первого нажатия поверх страницы — «Переходим к оплате…»: экран
 *     перехватывает все тапы, пока браузер не уйдёт в кассу.
 *   - Не ушли (ошибка, «уже открыто») — экран снимается сразу, кнопки снова
 *     работают. Ушли, но вернулись кнопкой «Назад» (страница из bfcache) или
 *     переход так и не случился за FALLBACK_MS — тоже снимается.
 *
 * Экран — обычный DOM, без React: он нужен на любой странице, откуда бы ни
 * позвали оплату. Вложенные вызовы (buyCalcUnlock → createPrimaryPayment)
 * идут через незащищённые *Now-функции, иначе внутренний ждал бы внешний.
 */

export const CHECKOUT_OVERLAY_ID = 'checkout-guard-overlay';
export const CHECKOUT_OVERLAY_TEXT = 'Переходим к оплате…';
const FALLBACK_MS = 15000;

let inflight = null;
let fallbackTimer = null;

function showOverlay() {
  if (typeof document === 'undefined' || !document.body) return;
  if (document.getElementById(CHECKOUT_OVERLAY_ID)) return;
  const el = document.createElement('div');
  el.id = CHECKOUT_OVERLAY_ID;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.setAttribute('data-testid', 'checkout-guard');
  el.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:2147483647', 'display:flex',
    'align-items:center', 'justify-content:center', 'background:rgba(255,255,255,0.72)',
    'touch-action:none', 'cursor:progress',
  ].join(';');
  const box = document.createElement('div');
  box.style.cssText = [
    'display:flex', 'align-items:center', 'gap:10px', 'padding:14px 20px',
    'border-radius:14px', 'background:#fff', 'box-shadow:0 6px 24px rgba(15,23,42,0.18)',
    'font:600 15px/1.3 system-ui,-apple-system,sans-serif', 'color:#0f172a',
  ].join(';');
  const spinner = document.createElement('span');
  spinner.setAttribute('aria-hidden', 'true');
  spinner.style.cssText = [
    'width:18px', 'height:18px', 'border-radius:50%', 'border:2px solid #cbd5e1',
    'border-top-color:#0f172a', 'animation:checkout-guard-spin 0.8s linear infinite',
  ].join(';');
  const style = document.createElement('style');
  style.textContent = '@keyframes checkout-guard-spin{to{transform:rotate(360deg)}}';
  const text = document.createElement('span');
  text.textContent = CHECKOUT_OVERLAY_TEXT;
  box.appendChild(style);
  box.appendChild(spinner);
  box.appendChild(text);
  el.appendChild(box);
  document.body.appendChild(el);
}

function hideOverlay() {
  if (typeof document === 'undefined') return;
  const el = document.getElementById(CHECKOUT_OVERLAY_ID);
  if (el && el.parentNode) el.parentNode.removeChild(el);
}

/** Снять блокировку: покупку снова можно начать. */
export function releaseCheckout() {
  if (fallbackTimer) clearTimeout(fallbackTimer);
  fallbackTimer = null;
  inflight = null;
  hideOverlay();
}

/** Создаётся ли сейчас платёж. */
export function checkoutInProgress() {
  return inflight !== null;
}

/**
 * Запустить покупку одной на страницу.
 * run — создаёт платёж; goesToPayment(result) — уходит ли вызывающий по
 * результату в кассу (тогда экран держим до перехода).
 */
export function singleCheckout(run, goesToPayment) {
  if (inflight) return inflight;
  showOverlay();
  if (fallbackTimer) clearTimeout(fallbackTimer);
  fallbackTimer = null;
  const p = Promise.resolve()
    .then(run)
    .then((result) => {
      if (inflight === p) {
        inflight = null;
        // Уходим в кассу: экран остаётся и держит тапы до перехода.
        if (goesToPayment(result)) fallbackTimer = setTimeout(releaseCheckout, FALLBACK_MS);
        else releaseCheckout();
      }
      return result;
    }, (err) => {
      if (inflight === p) releaseCheckout();
      throw err;
    });
  inflight = p;
  return p;
}

// Вернулись со страницы кассы «Назад»: страница поднята из bfcache вместе с
// экраном — снимаем, иначе покупка заблокирована навсегда.
if (typeof window !== 'undefined' && window.addEventListener) {
  window.addEventListener('pageshow', (e) => { if (e && e.persisted) releaseCheckout(); });
}
